// @flow
import { css } from 'styled-components';
import { breakPointSizes } from '../config';

// HTML markup for React
const createMarkup = (html: string) => ({ __html: html });

// Media query "mixins" for styled components
type MediaSizesType = {
  phone: (...params: Array<any>) => string,
  tablet: (...params: Array<any>) => string,
  desktop: (...params: Array<any>) => string,
  mobileMenu: (...params: Array<any>) => string,
};

type ScreenMixinType = {
  atLeast: MediaSizesType,
  smallerThan: MediaSizesType,
};

const screen: ScreenMixinType = {
  smallerThan: Object.keys(breakPointSizes).reduce((acc, label) => {
    acc[label] = (...args) => css`
      @media (max-width: ${breakPointSizes[label] - 0.02}px) {
        ${css(...args)}
      }
    `;
    return acc;
  }, {}),
  atLeast: Object.keys(breakPointSizes).reduce((acc, label) => {
    acc[label] = (...args) => css`
      @media (min-width: ${breakPointSizes[label]}px) {
        ${css(...args)}
      }
    `;
    return acc;
  }, {}),
};

// Cookie functions
const getCookie = (name: string) => {
  if (typeof window !== 'undefined') {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : undefined;
  }
  return undefined;
};

const deleteCookie = (name: string) => {
  if (typeof window !== 'undefined') {
    document.cookie = `${name}=; Max-Age=-99999999;`;
  }
};

// Scroll to anchor
const scrollToAnchor = (ev: any, anchor: string) => {
  ev.preventDefault();
  if (anchor === '#') {
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  } else {
    const element = document.querySelector(anchor);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  }
};

// Get Featured image from WPGraphQL
type WPImageType = {
  sourceUrl: string,
  mediaDetails: {
    sizes: [
      {
        name: string,
        sourceUrl: string,
      },
    ],
  },
};

const getFeaturedImage = (image: WPImageType, neededSize: string) => {
  const { sizes } = image.mediaDetails;
  const [foundImage] = sizes.filter((size) => size.name === neededSize);
  return foundImage ? foundImage.sourceUrl : image.sourceUrl;
};

// Strip tags
const stripTags = (text: string) =>
  text.replace(/&nbsp;/g, ' ').replace(/<\/?[^>]+(>|$)/g, '');

// Shorten text to latests '.', '!' or '?';
const shortenExcerpt = (text: string, size: number) => {
  const trimmedText = text.substr(0, size);
  const lastSentenceEnd = Math.max(
    trimmedText.lastIndexOf('. '),
    trimmedText.lastIndexOf('! '),
    trimmedText.lastIndexOf('? '),
    trimmedText.lastIndexOf('; '),
  );
  if (lastSentenceEnd < size / 2) {
    return `${trimmedText.substr(
      0,
      Math.min(trimmedText.length, trimmedText.lastIndexOf(' ')),
    )}…`;
  }
  return trimmedText.substr(
    0,
    Math.min(trimmedText.length, lastSentenceEnd + 1),
  );
};

export {
  createMarkup,
  screen,
  getCookie,
  deleteCookie,
  scrollToAnchor,
  getFeaturedImage,
  stripTags,
  shortenExcerpt,
};
